import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import type {
  Automation,
  AutomationDetail,
  AutomationNodeDef,
  AutomationExecution,
  AutomationExecutionDetail,
  CanvasSavePayload,
  LookupCaseType,
  LookupUser,
  LookupFieldDef,
  LookupConnectorInstance,
  ValidationResult,
  TestRunResult,
} from '../types/automations';

/* ─────────────────────────────────────────────
 * List all automations (admin table)
 * ───────────────────────────────────────────── */
export function useAutomations() {
  return useQuery<Automation[]>({
    queryKey: ['automations'],
    queryFn: async () => {
      const { data } = await api.get('/api/automations');
      if (data.success) return data.data as Automation[];
      return [];
    },
  });
}

/* ─────────────────────────────────────────────
 * Get single automation (full detail with nodes + edges)
 * ───────────────────────────────────────────── */
export function useAutomation(id: number | null) {
  return useQuery<AutomationDetail | null>({
    queryKey: ['automation', id],
    queryFn: async () => {
      if (!id) return null;
      const { data } = await api.get(`/api/automations/${id}`);
      if (data.success) return data.data as AutomationDetail;
      return null;
    },
    enabled: !!id,
  });
}

/* ─────────────────────────────────────────────
 * Node definitions (for palette)
 * ───────────────────────────────────────────── */
export function useNodeDefs() {
  return useQuery<AutomationNodeDef[]>({
    queryKey: ['automationNodeDefs'],
    queryFn: async () => {
      const { data } = await api.get('/api/automations/node-defs');
      if (data.success) return data.data as AutomationNodeDef[];
      return [];
    },
    staleTime: 5 * 60_000,
  });
}

/* ─────────────────────────────────────────────
 * Create automation
 * ───────────────────────────────────────────── */
export function useCreateAutomation() {
  const qc = useQueryClient();
  return useMutation<
    Automation,
    Error,
    { name?: string; description?: string; case_type_id?: number }
  >({
    mutationFn: async (payload) => {
      const { data } = await api.post('/api/automations', payload);
      return data.data as Automation;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
  });
}

/* ─────────────────────────────────────────────
 * Update automation metadata
 * ───────────────────────────────────────────── */
export function useUpdateAutomation() {
  const qc = useQueryClient();
  return useMutation<
    Automation,
    Error,
    { id: number; name?: string; description?: string; case_type_id?: number }
  >({
    mutationFn: async ({ id, ...payload }) => {
      const { data } = await api.put(`/api/automations/${id}`, payload);
      return data.data as Automation;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['automations'] });
      qc.invalidateQueries({ queryKey: ['automation', vars.id] });
    },
  });
}

/* ─────────────────────────────────────────────
 * Delete automation
 * ───────────────────────────────────────────── */
export function useDeleteAutomation() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await api.delete(`/api/automations/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
  });
}

/* ─────────────────────────────────────────────
 * Toggle active / inactive
 * ───────────────────────────────────────────── */
export function useToggleAutomation() {
  const qc = useQueryClient();
  return useMutation<{ id: number; is_active: boolean }, Error, number>({
    mutationFn: async (id) => {
      const { data } = await api.patch(`/api/automations/${id}/toggle`);
      return data.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['automations'] });
      qc.invalidateQueries({ queryKey: ['automation', id] });
    },
  });
}

/* ─────────────────────────────────────────────
 * Duplicate automation
 * ───────────────────────────────────────────── */
export function useDuplicateAutomation() {
  const qc = useQueryClient();
  return useMutation<Automation, Error, number>({
    mutationFn: async (id) => {
      const { data } = await api.post(`/api/automations/${id}/duplicate`);
      return data.data as Automation;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
  });
}

/* ─────────────────────────────────────────────
 * Save canvas (full node + edge state)
 * ───────────────────────────────────────────── */
export function useSaveCanvas() {
  const qc = useQueryClient();
  return useMutation<AutomationDetail, Error, { id: number; canvas: CanvasSavePayload }>({
    mutationFn: async ({ id, canvas }) => {
      const { data } = await api.put(`/api/automations/${id}/canvas`, canvas);
      return data.data as AutomationDetail;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['automation', vars.id] });
      qc.invalidateQueries({ queryKey: ['automations'] });
    },
  });
}

/* ─────────────────────────────────────────────
 * Execution history
 * ───────────────────────────────────────────── */
export function useAutomationExecutions(automationId: number | null) {
  return useQuery<AutomationExecution[]>({
    queryKey: ['automationExecutions', automationId],
    queryFn: async () => {
      if (!automationId) return [];
      const { data } = await api.get(`/api/automations/${automationId}/executions`);
      if (data.success) return data.data as AutomationExecution[];
      return [];
    },
    enabled: !!automationId,
  });
}

/* ─────────────────────────────────────────────
 * Execution detail (with per-node steps)
 * ───────────────────────────────────────────── */
export function useExecutionDetail(automationId: number | null, executionId: number | null) {
  return useQuery<AutomationExecutionDetail | null>({
    queryKey: ['executionDetail', automationId, executionId],
    queryFn: async () => {
      if (!automationId || !executionId) return null;
      const { data } = await api.get(`/api/automations/${automationId}/executions/${executionId}`);
      if (data.success) return data.data as AutomationExecutionDetail;
      return null;
    },
    enabled: !!automationId && !!executionId,
  });
}

/* ─────────────────────────────────────────────
 * Validate automation
 * ───────────────────────────────────────────── */
export function useValidateAutomation() {
  return useMutation<ValidationResult, Error, number>({
    mutationFn: async (id) => {
      const { data } = await api.post(`/api/automations/${id}/validate`);
      return data.data as ValidationResult;
    },
  });
}

/* ─────────────────────────────────────────────
 * Test run (dry run)
 * ───────────────────────────────────────────── */
export function useTestRun() {
  return useMutation<TestRunResult, Error, number>({
    mutationFn: async (id) => {
      const { data } = await api.post(`/api/automations/${id}/test-run`);
      return data.data as TestRunResult;
    },
  });
}

/* ─────────────────────────────────────────────
 * Lookups for node property forms
 * ───────────────────────────────────────────── */
export function useLookupCaseTypes() {
  return useQuery<LookupCaseType[]>({
    queryKey: ['automationLookupCaseTypes'],
    queryFn: async () => {
      const { data } = await api.get('/api/automations/lookups/case-types');
      return data.success ? (data.data as LookupCaseType[]) : [];
    },
    staleTime: 5 * 60_000,
  });
}

export function useLookupUsers() {
  return useQuery<LookupUser[]>({
    queryKey: ['automationLookupUsers'],
    queryFn: async () => {
      const { data } = await api.get('/api/automations/lookups/users');
      return data.success ? (data.data as LookupUser[]) : [];
    },
    staleTime: 5 * 60_000,
  });
}

export function useLookupFieldDefs() {
  return useQuery<LookupFieldDef[]>({
    queryKey: ['automationLookupFieldDefs'],
    queryFn: async () => {
      const { data } = await api.get('/api/automations/lookups/field-definitions');
      return data.success ? (data.data as LookupFieldDef[]) : [];
    },
    staleTime: 5 * 60_000,
  });
}

export function useLookupConnectorInstances() {
  return useQuery<LookupConnectorInstance[]>({
    queryKey: ['automationLookupConnectorInstances'],
    queryFn: async () => {
      const { data } = await api.get('/api/automations/lookups/connector-instances');
      return data.success ? (data.data as LookupConnectorInstance[]) : [];
    },
    staleTime: 5 * 60_000,
  });
}
